import { FabricObject, Image as FabricImage } from "fabric";
import annotateTick from "@/assets/images/icons/misc/32px/AnnotateTick32.svg?url";
import annotateCross from "@/assets/images/icons/misc/32px/AnnotateCross32.svg?url";
import annotateCircle from "@/assets/images/icons/misc/32px/AnnotateCircle32.svg?url";
import assessmentTick from "@/assets/images/icons/misc/32px/AssessmentTick32.svg?url";
import assessmentCross from "@/assets/images/icons/misc/32px/AssessmentCross32.svg?url";
import assessmentCircle from "@/assets/images/icons/misc/32px/AssessmentCircle32.svg?url";

export const STAMP_ICON_URLS_TEACHER: Record<string, string> = {
  "shape-1": assessmentTick,
  "shape-2": assessmentCross,
  "shape-3": assessmentCircle,
};

export const STAMP_ICON_URLS_STUDENT: Record<string, string> = {
  "shape-1": annotateTick,
  "shape-2": annotateCross,
  "shape-3": annotateCircle,
};

interface StampObjectData {
  elementType?: "stamp";
  stampShape?: string;
}

export type StampObject = FabricObject & StampObjectData;

const stampImages = new Map<string, HTMLImageElement>();
const stampImagePromises = new Map<string, Promise<HTMLImageElement>>();

export async function preloadStampIcons(stampIcons: Record<string, string>): Promise<void> {
  await Promise.all([...new Set(Object.values(stampIcons))].map(loadStampImage));
}

function loadStampImage(src: string): Promise<HTMLImageElement> {
  const cachedImage = stampImages.get(src);
  if (cachedImage) return Promise.resolve(cachedImage);

  const existingPromise = stampImagePromises.get(src);
  if (existingPromise) return existingPromise;

  const imagePromise = loadImage(src).then(
    (image) => {
      stampImages.set(src, image);
      stampImagePromises.delete(src);
      return image;
    },
    (error: unknown) => {
      stampImagePromises.delete(src);
      throw error;
    },
  );
  stampImagePromises.set(src, imagePromise);
  return imagePromise;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = (e) => {
      console.log(e);
      reject(new Error(`Unable to load stamp icon: ${src}`));
    };
    image.src = src;
  });
}

// Creates an icon-backed stamp scaled to STAMP_SIZE, centre origin.
export function createStamp(
  shape: string,
  stampIcons: Record<string, string> = STAMP_ICON_URLS_TEACHER,
): StampObject {
  const src = stampIcons[shape];
  const image = src ? stampImages.get(src) : undefined;
  if (!image) throw new Error("Stamp icons have not been preloaded");

  const stamp = new FabricImage(image, {
    originX: "center",
    originY: "center",
    lockRotation: true,
  }) as StampObject;

  stamp.scaleToWidth(40);
  stamp.elementType = "stamp";
  stamp.stampShape = shape;
  stamp.set({ erasable: false });
  stamp.setControlsVisibility({ mtr: false });
  return stamp;
}
